/**
 * Hook and utilities for LGPD-compliant cookie consent.
 *
 * Scripts that depend on cookie categories should use:
 *   const { analytics, marketing } = useCookieConsent();
 *   useEffect(() => { if (analytics) loadAnalyticsScript(); }, [analytics]);
 *
 * For non-React contexts (e.g. index.html inline scripts), listen to:
 *   window.addEventListener("cookie-consent-update", (e) => { ... });
 */

import { useState, useEffect, useCallback } from "react";

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const COOKIE_CONSENT_KEY = "acelerai_cookie_consent";
export const COOKIE_PREFERENCES_KEY = "acelerai_cookie_preferences";
export const COOKIE_CONSENT_EVENT = "cookie-consent-update";

/** Read current preferences from localStorage (defaults to all denied except necessary). */
export function getCookiePreferences(): CookiePreferences {
  const defaults: CookiePreferences = { necessary: true, analytics: false, marketing: false };
  try {
    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<CookiePreferences>;
      return { ...defaults, ...parsed, necessary: true };
    }
  } catch {
    // ignore parse errors
  }
  return defaults;
}

/** Whether user has already made a consent choice. */
export function hasConsentBeenGiven(): boolean {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === "true";
}

/** Save preferences, mark consent as given, and broadcast event. */
export function savePreferences(prefs: CookiePreferences) {
  localStorage.setItem(COOKIE_CONSENT_KEY, "true");
  localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_EVENT, { detail: prefs })
  );
}

/**
 * React hook that returns the current cookie preferences and re-renders
 * whenever the user updates them (via CookieConsent banner or elsewhere).
 */
export function useCookieConsent(): CookiePreferences {
  const [prefs, setPrefs] = useState<CookiePreferences>(getCookiePreferences);

  const handleUpdate = useCallback((e: Event) => {
    const detail = (e as CustomEvent<CookiePreferences>).detail;
    setPrefs(detail);
  }, []);

  useEffect(() => {
    window.addEventListener(COOKIE_CONSENT_EVENT, handleUpdate);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handleUpdate);
  }, [handleUpdate]);

  return prefs;
}
